/**
 * First-run guide — pure logic + persistence adapter.
 *
 * This module owns the *rules* of the new-tenant activation guide with zero
 * React or DOM dependencies, so the behaviour (step order, completion, resume)
 * is fully unit-testable. The React provider (FirstRunGuideContext) wires real
 * data signals + storage into these functions.
 *
 * Persistence is deliberately behind the `GuideStore` interface. Today it is
 * backed by localStorage (per tenant). To move to DB persistence later, supply
 * a different `GuideStore` implementation to the provider — nothing else needs
 * to change.
 *
 * Progression model: `leadingEdge` (the first not-yet-done step) is always
 * derived fresh from real signals — it's what "reacts to state" means. The
 * user's on-screen position defaults to following it. The moment they step
 * backward to review a completed step, that view is *pinned* so background
 * state changes elsewhere don't yank it away — moving forward again (Next,
 * a CTA, or a dot at/beyond the edge) un-pins and resumes following the edge.
 */

// ── Status + persisted shape ────────────────────────────────────────────────

export type GuideStatus = 'active' | 'dismissed' | 'completed'

export interface GuidePersistedState {
  status: GuideStatus
  /** View position while `pinned`; ignored while following the leading edge. */
  currentStep: number
  /** True while the user is deliberately reviewing an earlier completed step. */
  pinned: boolean
  /** Soft-step ids the user has explicitly actioned (e.g. clicked a CTA). */
  visited: string[]
  /** Whether the one-time completion moment has already been shown. */
  celebrated: boolean
  /** Schema tag so we can evolve the shape without misreading old data. */
  version: number
}

export const GUIDE_VERSION = 1

export function defaultGuideState(): GuidePersistedState {
  return { status: 'active', currentStep: 0, pinned: false, visited: [], celebrated: false, version: GUIDE_VERSION }
}

// ── Data signals (derived from the workspace, not the user's clicks) ─────────

export interface GuideSignals {
  hasBlueprint: boolean          // a published blueprint exists
  hasEmployee: boolean           // at least one employee created
  hasLaunched: boolean           // a journey has been assigned — journey_steps exist (new model) or tasks.employee_id is set (legacy)
  hasReadinessForecast: boolean  // the forecast engine has computed a state for at least one journey
  hasTeamMember: boolean         // a teammate/manager joined (profiles.role = member)
}

export function emptySignals(): GuideSignals {
  return { hasBlueprint: false, hasEmployee: false, hasLaunched: false, hasReadinessForecast: false, hasTeamMember: false }
}

/**
 * A hire counts as "launched" via either source of truth: the current
 * `journeys`/`journey_steps` model, or the legacy `tasks.employee_id`
 * assignment. Checking only the legacy path left the guide stuck on the
 * "launch" step forever for journeys created through Apply Blueprint, which
 * only ever writes `journeys`/`journey_steps`.
 */
export function computeHasLaunched(counts: { legacyLaunchedTaskCount: number; journeyCount: number }): boolean {
  return counts.legacyLaunchedTaskCount > 0 || counts.journeyCount > 0
}

/**
 * The first hire not yet covered by either launch source — used to target the
 * "launch" step's CTA at the specific profile that still needs a blueprint
 * applied, rather than a generic list page.
 */
export function findLaunchCandidate(
  employees: { id: string; name: string }[],
  launchedEmployeeIds: string[],
): { id: string; name: string } | null {
  const launched = new Set(launchedEmployeeIds)
  return employees.find((e) => !launched.has(e.id)) ?? null
}

type SignalKey = keyof GuideSignals

// ── Step definitions ─────────────────────────────────────────────────────────

export interface GuideStep {
  id: string
  title: string
  body: string
  /** CTA label; absent for the welcome step which only has Next. */
  cta?: string
  /** Route the CTA navigates to. */
  ctaPath?: string
  /** Optional steps don't block completion (e.g. invite a teammate). */
  optional?: boolean
  /** Step is done when this workspace signal is true. */
  doneSignal?: SignalKey
  /**
   * When true, the step is also marked done once the user has actioned it
   * (clicked its CTA / pressed Next). Used for steps whose "completion" is the
   * act of reviewing something rather than a hard data change.
   */
  softComplete?: boolean
}

/**
 * The new-tenant journey. Order matters — this is the canonical sequence.
 * Copy leads with the outcome, then the action — never a bare "go to X".
 */
export const GUIDE_STEPS: GuideStep[] = [
  {
    id: 'welcome',
    title: 'Get your first onboarding live',
    body: "A few short steps and your first hire's onboarding will be running. You can hide this guide any time — it'll pick up where you left off.",
    softComplete: true,
  },
  {
    id: 'blueprint',
    title: 'Your onboarding process, already built',
    body: 'Every hire needs a plan before day one — we’ve pre-built yours as a reusable blueprint. Review it now; tailor it whenever you like, and every future hire follows it automatically.',
    cta: 'Review blueprint',
    ctaPath: '/blueprints',
    doneSignal: 'hasBlueprint',
    softComplete: true,
  },
  {
    id: 'people',
    title: 'This is where every hire lives',
    body: 'People is the one place every hire — joining, moving, or returning — lives for good, not just today’s. Take a look before adding your first.',
    cta: 'Go to People',
    ctaPath: '/people',
    doneSignal: 'hasEmployee',
    softComplete: true,
  },
  {
    id: 'employee',
    title: 'One employee is all it takes to start',
    body: 'Once your first employee exists, ODOnboard can coordinate every team involved in their onboarding — automatically, from day one.',
    cta: 'Add employee',
    ctaPath: '/people?action=add',
    doneSignal: 'hasEmployee',
  },
  {
    id: 'launch',
    title: 'Turn a hire into a coordinated plan',
    body: 'Launching schedules every task, for every team, against your hire’s real start date — nothing left to chase manually. Open their profile and apply a blueprint to launch it.',
    cta: 'Go to People',
    ctaPath: '/people',
    doneSignal: 'hasLaunched',
  },
  {
    id: 'readiness',
    title: 'Know if Day 1 is actually on track',
    body: 'Once launched, ODOnboard forecasts whether every task will finish in time — open the profile to see it for your first hire.',
    cta: 'View readiness',
    ctaPath: '/people',
    doneSignal: 'hasReadinessForecast',
    softComplete: true,
  },
  {
    id: 'team',
    title: "Onboarding isn't a solo job",
    body: 'Bring in the people who actually own tasks — HR, IT, a hiring manager — so the work is shared, not stacked on you.',
    cta: 'Invite team',
    ctaPath: '/admin/team-members',
    optional: true,
    doneSignal: 'hasTeamMember',
    softComplete: true,
  },
]

// ── Completion logic ───────────────────────────────────────────────────────────

export function isStepDone(step: GuideStep, signals: GuideSignals, visited: string[]): boolean {
  if (step.doneSignal && signals[step.doneSignal]) return true
  if (step.softComplete && visited.includes(step.id)) return true
  return false
}

/** Required (non-optional) steps must all be done for the guide to complete. */
export function allRequiredDone(signals: GuideSignals, visited: string[]): boolean {
  return GUIDE_STEPS.filter(s => !s.optional).every(s => isStepDone(s, signals, visited))
}

/** Count of done steps (incl. optional) — for the progress indicator. */
export function doneCount(signals: GuideSignals, visited: string[]): number {
  return GUIDE_STEPS.filter(s => isStepDone(s, signals, visited)).length
}

/** First step index that isn't done yet; falls back to the last index. */
export function firstIncompleteIndex(signals: GuideSignals, visited: string[]): number {
  const idx = GUIDE_STEPS.findIndex(s => !isStepDone(s, signals, visited))
  return idx === -1 ? GUIDE_STEPS.length - 1 : idx
}

/**
 * Resolve which step is on screen right now.
 *
 * Not pinned (the default): always the leading edge — the guide follows real
 * progress, so finishing an action elsewhere advances the card without a
 * click. Pinned: the user deliberately stepped back to review a completed
 * step, and that view is respected until they explicitly move again.
 */
export function resolveActiveIndex(persisted: GuidePersistedState, signals: GuideSignals): number {
  if (!persisted.pinned) return firstIncompleteIndex(signals, persisted.visited)
  return Math.min(Math.max(persisted.currentStep, 0), GUIDE_STEPS.length - 1)
}

// ── Persistence (swappable: localStorage today, DB later) ───────────────────

export interface GuideStore {
  load(tenantId: string): GuidePersistedState | null
  save(tenantId: string, state: GuidePersistedState): void
  clear(tenantId: string): void
}

function storageKey(tenantId: string): string {
  return `odonboard:first-run-guide:v${GUIDE_VERSION}:${tenantId}`
}

export const localStorageGuideStore: GuideStore = {
  load(tenantId) {
    try {
      const raw = localStorage.getItem(storageKey(tenantId))
      if (!raw) return null
      const parsed = JSON.parse(raw) as Partial<GuidePersistedState>
      if (parsed.version !== GUIDE_VERSION) return null
      return {
        status: parsed.status ?? 'active',
        currentStep: typeof parsed.currentStep === 'number' ? parsed.currentStep : 0,
        pinned: parsed.pinned ?? false,
        visited: Array.isArray(parsed.visited) ? parsed.visited : [],
        celebrated: parsed.celebrated ?? false,
        version: GUIDE_VERSION,
      }
    } catch {
      return null
    }
  },
  save(tenantId, state) {
    try {
      localStorage.setItem(storageKey(tenantId), JSON.stringify(state))
    } catch {
      /* storage full / unavailable — degrade silently */
    }
  },
  clear(tenantId) {
    try {
      localStorage.removeItem(storageKey(tenantId))
    } catch {
      /* ignore */
    }
  },
}
